import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { StoreApplication, StoreApplicationStatus } from '../../types/index';
import api from '../../services/api';
import { getApiErrorMessage } from '../../services/api';

// ステータスのラベルと色
const statusConfig: Record<StoreApplicationStatus, { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }> = {
  pending: { label: '未対応', color: 'warning' },
  in_progress: { label: '対応中', color: 'info' },
  completed: { label: '完了', color: 'success' },
  rejected: { label: '却下', color: 'error' },
};

const lineAccountStatusLabel = (status: string) => {
  switch (status) {
    case 'has': return '持っている';
    case 'not_has': return '持っていない';
    case 'unknown': return 'わからない';
    default: return '-';
  }
};

const StoreApplicationTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [applications, setApplications] = useState<StoreApplication[]>([]);
  const [loading, setLoading] = useState(true);
  // 一覧取得の失敗（一覧の代わりに表示する）と、詳細モーダル内の操作の失敗（モーダル内に表示する）は
  // 別物。同じ state を使うとステータス更新に失敗しただけで申込一覧が丸ごと消える
  // （動作確認NG: 8章 8-2 企業管理）
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<StoreApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<StoreApplicationStatus>('pending');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.storeApplication.getAll();
      setApplications(data);
    } catch (err) {
      console.error('[StoreApplicationTab] API error:', err);
      setError('店舗申込情報の取得に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (application: StoreApplication) => {
    setSelectedApplication(application);
    setEditingStatus(application.status);
    // 前回の申込で出したメッセージを持ち越さない
    setSaveError(null);
    setSuccessMessage(null);
    setWarningMessage(null);
    setDetailOpen(true);
  };

  // 完了は終端ステータス。詳細モーダルのステータス操作を一括で止めるために使う
  const isCompleted = selectedApplication?.status === 'completed';

  const handleStatusSave = async () => {
    if (!selectedApplication || editingStatus === selectedApplication.status) return;
    // UIは disabled にしてあるが、完了済みからの変更はサーバも400で拒否するため二重に止める
    if (selectedApplication.status === 'completed') return;
    setSaving(true);
    setSaveError(null);
    setSuccessMessage(null);
    setWarningMessage(null);
    try {
      const updated = await api.storeApplication.updateStatus(
        selectedApplication.id,
        { status: editingStatus },
      );

      // 完了時のメッセージ表示
      if (editingStatus === 'completed') {
        const msgs: string[] = ['店舗が作成されました。'];
        if (updated.created_company_id) {
          msgs.push('企業が登録されました。');
        }
        if (updated.management_account_created) {
          msgs.push('管理アカウントが自動作成されました。');
        }
        setSuccessMessage(msgs.join(' '));

        if (updated.firebase_account_warning) {
          setWarningMessage(updated.firebase_account_warning);
        }
      }

      setSelectedApplication(updated);
      setApplications((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (err) {
      // 一覧は変化させず、操作したモーダル内にだけエラーを出す
      setSaveError(getApiErrorMessage(err, 'ステータスの更新に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#3C3C3C' }}>
        店舗申込一覧
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : applications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">申込データがありません</Typography>
        </Paper>
      ) : isMobile ? (
        /* モバイル: カード形式 */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {applications.map((app) => (
            <Card
              key={app.id}
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => handleViewDetail(app)}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#3C3C3C' }}>
                      {app.store_name}
                    </Typography>
                    {app.is_company_application && (
                      <Chip icon={<BusinessIcon />} label="企業" size="small" color="primary" variant="outlined" />
                    )}
                  </Box>
                  <Chip
                    label={statusConfig[app.status].label}
                    color={statusConfig[app.status].color}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">{app.contact_name}</Typography>
                <Typography variant="body2" color="text.secondary">{app.email}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  申込日: {formatDate(app.created_at)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        /* デスクトップ: テーブル形式 */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>店舗名</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>種別</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>担当者名</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>メールアドレス</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>電話番号</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>LINE所持</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>ステータス</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }}>申込日</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#3C3C3C' }} align="center">詳細</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((app) => (
                <TableRow key={app.id} hover>
                  <TableCell sx={{ color: '#3C3C3C' }}>{app.store_name}</TableCell>
                  <TableCell>
                    {app.is_company_application ? (
                      <Chip icon={<BusinessIcon />} label="企業" size="small" color="primary" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">個人</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#3C3C3C' }}>{app.contact_name}</TableCell>
                  <TableCell sx={{ color: '#3C3C3C' }}>{app.email}</TableCell>
                  <TableCell sx={{ color: '#3C3C3C' }}>{app.phone}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#3C3C3C' }}>
                      {lineAccountStatusLabel(app.line_account_status)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusConfig[app.status].label}
                      color={statusConfig[app.status].color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#3C3C3C' }}>{formatDate(app.created_at)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleViewDetail(app)}>
                      <VisibilityIcon sx={{ color: '#1976d2' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 詳細ダイアログ */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#3C3C3C' }}>
            店舗申込詳細
          </Typography>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* ステータス更新の失敗（モーダルは閉じず、背後の一覧も変化させない） */}
              {saveError && (
                <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>
              )}

              {/* 成功メッセージ */}
              {successMessage && (
                <Alert severity="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>
              )}

              {/* Firebaseアカウント作成失敗の警告 */}
              {warningMessage && (
                <Alert severity="warning" onClose={() => setWarningMessage(null)}>{warningMessage}</Alert>
              )}

              {/* ステータス編集。completed は終端ステータスなので操作させない
                  （店舗・企業・Firebaseアカウントを実体として作成済みで、ステータスだけ
                  巻き戻すと画面表示と実体が乖離する。サーバも400で拒否するため、
                  ここで操作できると「押せるのに失敗する」だけになる） */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl sx={{ minWidth: 160 }} disabled={isCompleted}>
                  <InputLabel>ステータス</InputLabel>
                  <Select
                    value={editingStatus}
                    label="ステータス"
                    onChange={(e) => setEditingStatus(e.target.value as StoreApplicationStatus)}
                  >
                    <MenuItem value="pending">未対応</MenuItem>
                    <MenuItem value="in_progress">対応中</MenuItem>
                    <MenuItem value="completed">完了</MenuItem>
                    <MenuItem value="rejected">却下</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleStatusSave}
                  disabled={saving || isCompleted || editingStatus === selectedApplication.status}
                  sx={{
                    bgcolor: '#3E4298',
                    '&:hover': { bgcolor: '#4b49d6' },
                  }}
                >
                  {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '保存'}
                </Button>
              </Box>
              {isCompleted && (
                <Alert severity="info">
                  この申込は完了済みのため、ステータスは変更できません。
                  店舗の利用を停止する場合は「店舗管理」タブの解約予約をご利用ください。
                </Alert>
              )}

              <Divider />

              <DetailField label="店舗名" value={selectedApplication.store_name} />
              <DetailField label="店舗住所" value={selectedApplication.store_address} />
              <DetailField label="担当者名" value={selectedApplication.contact_name} />
              <DetailField label="メールアドレス" value={selectedApplication.email} />
              <DetailField label="電話番号" value={selectedApplication.phone} />

              <Divider />

              <DetailField
                label="LINE公式アカウント所持状況"
                value={lineAccountStatusLabel(selectedApplication.line_account_status)}
              />
              {selectedApplication.line_account_status === 'has' && (
                <DetailField
                  label="アカウント情報の把握"
                  value={selectedApplication.account_info_known === 'yes' ? 'はい' : 'いいえ'}
                />
              )}

              {/* 企業情報（企業申込の場合のみ表示） */}
              {selectedApplication.is_company_application && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#3C3C3C', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: 18 }} /> 企業情報
                  </Typography>
                  <DetailField label="企業名" value={selectedApplication.company_name || '-'} />
                  <DetailField label="企業所在地" value={selectedApplication.company_address || '-'} />
                  <DetailField label="企業電話番号" value={selectedApplication.company_phone || '-'} />
                  <DetailField label="代表者名" value={selectedApplication.representative_name || '-'} />
                  <DetailField label="業種" value={selectedApplication.business_type || '-'} />
                </>
              )}

              <Divider />

              <DetailField label="申込日時" value={formatDate(selectedApplication.created_at)} />
              <DetailField label="更新日時" value={formatDate(selectedApplication.updated_at)} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// 詳細表示用のフィールドコンポーネント
const DetailField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={{ color: '#3C3C3C' }}>{value}</Typography>
  </Box>
);

export default StoreApplicationTab;
